import { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { axiosClient } from '../../../library/Service/axiosClient';
import { ACTIVITY_CATEGORIES } from './activityCategories';

// The CSV carries codes, not labels — uploading "Admissions & Marketing" would fall back to OTHERS.
const CATEGORY_CODES = ACTIVITY_CATEGORIES.map(({ code }) => code).join(', ');

// ─── CSV helpers ──────────────────────────────────────────────────────────────

function unquote(s) {
  let t = s.trim();
  if (t.startsWith('"') && t.endsWith('"') && t.length >= 2) t = t.slice(1, -1);
  return t.replace(/""/g, '"');
}

function splitCSVLine(line) {
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (ch === ',' && !inQ) {
      out.push(cur); cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map(unquote);
}

function parseCSV(text) {
  const lines = text.replace(/^﻿/, '').split(/\r\n|\n|\r/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const rawHeaders = splitCSVLine(lines[0]);
  const headers = rawHeaders.map(h => h.toLowerCase().trim());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitCSVLine(lines[i]);
    const row = {};
    headers.forEach((h, idx) => { row[h] = cells[idx] ?? ''; });
    rows.push({ _rowNumber: i + 1, ...row });
  }
  return { headers, rows };
}

// ─── Validators ───────────────────────────────────────────────────────────────

function isValidYYYYMMDD(val) {
  if (!val || String(val).trim() === '') return false;
  const s = String(val).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  return !isNaN(new Date(y, m - 1, d).getTime());
}

// ─── Constants ────────────────────────────────────────────────────────────────

const REQUIRED_HEADERS = ['subject', 'email', 'phone', 'institute_id', 'event_date', 'open', 'click', 'mode', 'category', 'type'];
const MAX_ROWS = 2000;
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

// ─── Component ───────────────────────────────────────────────────────────────

function BulkUploadModal(props) {
  const { className, onSuccess, autoOpen = false, watchlistId = null, onAutoOpened } = props;
  const [show, setShow] = useState(() => {
    if (autoOpen) return true;
    try { return sessionStorage.getItem('activityLogAutoOpen') === '1'; } catch (_) { return false; }
  });
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const autoOpenConsumedRef = useRef(false);

  useEffect(() => {
    if (autoOpen) setShow(true);
  }, [autoOpen]);

  const consumeAutoOpen = () => {
    if (autoOpenConsumedRef.current) return;
    autoOpenConsumedRef.current = true;
    try { sessionStorage.removeItem('activityLogAutoOpen'); } catch (_) { /* ignore */ }
    onAutoOpened?.();
  };

  // Uploader info from session
  let update_by_userid = 0;
  let update_by_name = '';
  try {
    const u = JSON.parse(sessionStorage.getItem('user') || '{}');
    update_by_userid = u.userId || 0;
    update_by_name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  } catch (_) {}

  const resetFile = () => {
    setFile(null);
    setParsedRows([]);
    setParseError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    consumeAutoOpen();
    setShow(false);
    resetFile();
    setIsUploading(false);
    setResult(null);
  };

  const handleFileChange = (e) => {
    setResult(null);
    setParseError('');
    setParsedRows([]);
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }

    if (!f.name.toLowerCase().endsWith('.csv')) {
      setParseError('Invalid file format. Please upload a .csv file.');
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    if (f.size > MAX_SIZE) {
      setParseError('File too large. Max allowed size is 10 MB.');
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setFile(f);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target?.result ?? '');
      const { headers, rows } = parseCSV(text);

      const missing = REQUIRED_HEADERS.filter(h => !headers.includes(h));
      if (missing.length) {
        setParseError(`Missing required column(s): ${missing.join(', ')}`);
        setFile(null);
        if (inputRef.current) inputRef.current.value = '';
        return;
      }

      if (rows.length > MAX_ROWS) {
        setParseError(`File has ${rows.length} rows. Maximum allowed is ${MAX_ROWS}.`);
        setFile(null);
        if (inputRef.current) inputRef.current.value = '';
        return;
      }

      if (rows.length === 0) {
        setParseError('No data rows found in the CSV.');
        setFile(null);
        if (inputRef.current) inputRef.current.value = '';
        return;
      }

      // Validate event_date format: must be YYYY-MM-DD
      const badDateRows = rows.filter(r => !isValidYYYYMMDD(r['event_date']));
      if (badDateRows.length > 0) {
        const preview = badDateRows.slice(0, 5).map(r => `Row ${r._rowNumber} ("${r['event_date'] || 'empty'}")`).join(', ');
        const extra = badDateRows.length > 5 ? ` and ${badDateRows.length - 5} more` : '';
        setParseError(`event_date must be in YYYY-MM-DD format (e.g. 2026-01-15). Invalid in: ${preview}${extra}`);
        setFile(null);
        if (inputRef.current) inputRef.current.value = '';
        return;
      }

      setParsedRows(rows);
    };
    reader.readAsText(f);
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file || parsedRows.length === 0) {
      setResult({ success: false, message: 'Please select a valid CSV file.' });
      return;
    }

    setIsUploading(true);
    setResult(null);

    const rowsToSend = parsedRows.map(({ _rowNumber, ...r }) => r);
    const body = { rows: rowsToSend, update_by_userid, update_by_name };
    const parsedWatchlistId = watchlistId != null && String(watchlistId).trim() !== '' ? Number(watchlistId) : NaN;
    if (Number.isFinite(parsedWatchlistId) && parsedWatchlistId > 0) {
      body.watchlist_id = parsedWatchlistId;
    }

    try {
      const res = await axiosClient().post(
        `${process.env.REACT_APP_BROADCAST_SERVER}/referral/bulk-upload-activities-csv`,
        body
      );
      setResult(res.data);
      if (res.data?.insertedCount > 0) onSuccess?.();
    } catch (err) {
      setResult({
        success: false,
        message: err?.response?.data?.message || err?.message || 'Upload failed.',
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Button className={className} onClick={() => setShow(true)}>
        <i className="icon-upload-big-arrow text-white me-2 position-relative"></i>
        <span>Bulk Upload</span>
      </Button>

      {show && (
        <style>{`
          .activity-csv-upload-backdrop { z-index: 10040 !important; }
          .modal.loginModal { z-index: 10050 !important; }
        `}</style>
      )}

      <Modal
        className="loginModal"
        show={show}
        onHide={handleClose}
        onEntered={consumeAutoOpen}
        size="lg"
        centered
        style={{ zIndex: 10050 }}
        backdropClassName="activity-csv-upload-backdrop"
      >
        <div className="modal-header p-4 height-70 border-0 bg-gray-f2">
          <h5 className="modal-title">Bulk Upload Activities</h5>
          <Button variant="close" onClick={handleClose} />
        </div>

        <Modal.Body className="p-4">
          <form noValidate onSubmit={handleUpload}>
            <div className="row g-3">

              {/* File input */}
              <div className="col-12">
                <label className="name font-500 mb-2 d-flex align-items-center">
                  Upload File{' '}
                  <span className="ms-2 text-muted font-13 font-400">(.csv only, max 10 MB, max {MAX_ROWS} rows)</span>
                  <a
                    href="https://ags-images-bucket.s3.ap-south-1.amazonaws.com/images/sample_file_for_log.csv"
                    className="btn-darkblue height-32 px-3 font-400 text-decoration-none ms-2"
                    download
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>Download Sample</span>
                  </a>
                </label>
                <div className="file file--upload">
                  <label htmlFor="activity-csv-file" className="py-3">
                    <i className="material-icons img-fluid">
                      <img
                        alt="upload"
                        src="https://s3.ap-south-1.amazonaws.com/media.univariety.com/userreactapp/style-images/cloud-computing.svg"
                        width="30"
                      />
                    </i>
                    <div>Drag and drop here · CSV files only (Max 10 MB / Max 2,000 rows)</div>
                  </label>
                  <input
                    id="activity-csv-file"
                    ref={inputRef}
                    type="file"
                    accept=".csv"
                    className="d-block position-absolute top-0 w-100 h-100 opacity-0"
                    onChange={handleFileChange}
                  />
                </div>
              </div>

              {/* Parse error */}
              {parseError && (
                <div className="col-12">
                  <div className="alert alert-danger p-2 mb-0">
                    <i className="bi bi-exclamation-triangle-fill me-2" />
                    {parseError}
                  </div>
                </div>
              )}

              {/* File ready preview */}
              {file && parsedRows.length > 0 && !parseError && (
                <div className="col-12">
                  <div className="alert alert-info p-2 mb-0">
                    <b>Selected:</b> {file.name} &nbsp;|&nbsp; <b>{parsedRows.length}</b> data row(s) ready to upload.
                  </div>
                </div>
              )}

              {/* Result summary */}
              {result && (
                <>
                  {result.message && (
                    <div className="col-12">
                      <div className={`alert ${result.success ? 'alert-success' : 'alert-danger'} p-2 mb-0`}>
                        {result.message}
                      </div>
                    </div>
                  )}

                  {result.watchlistSync && (
                    <div className="col-12">
                      {result.watchlistSync.message && (
                        <div className={`alert ${result.watchlistSync.ran === false ? 'alert-warning' : 'alert-info'} p-2 mb-0`}>
                          {result.watchlistSync.message}
                        </div>
                      )}
                      {Array.isArray(result.watchlistSync.unknownUsers) && result.watchlistSync.unknownUsers.length > 0 && (
                        <ul className="mb-0 mt-2 font-13">
                          {result.watchlistSync.unknownUsers.map((u, idx) => (
                            <li key={idx}>{u}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {Array.isArray(result.failedRows) && result.failedRows.length > 0 && (
                    <div className="col-12">
                      <div className="border rounded p-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
                        <div className="font-13 font-500 mb-2 text-danger">
                          Failed Rows ({result.failedRows.length}):
                        </div>
                        {result.failedRows.slice(0, 100).map((r, idx) => (
                          <div key={idx} className="mb-2 font-13">
                            <b>Row {r.rowNumber}:</b>{' '}
                            {Array.isArray(r.errors) ? r.errors.join(' | ') : String(r.errors || '')}
                            {idx < result.failedRows.length - 1 && <hr className="my-1" />}
                          </div>
                        ))}
                        {result.failedRows.length > 100 && (
                          <div className="text-muted font-13">
                            …and {result.failedRows.length - 100} more failed rows.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Upload button — visible until result arrives */}
              {!result && (
                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-green max-135 mx-auto d-flex align-items-center gap-2"
                    disabled={isUploading || !!parseError || parsedRows.length === 0}
                  >
                    {isUploading && (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                    )}
                    <span>{isUploading ? 'Uploading…' : 'Upload'}</span>
                  </button>
                </div>
              )}

              {/* Column reference */}
              <div className="col-12 text-muted" style={{ fontSize: 12 }}>
                Required columns: <code>subject, email, phone, institute_id, event_date, open, click, mode, category, type</code>
                <br />
                <span className="text-danger fw-500">event_date :- yyyy-mm-dd</span> &nbsp;(e.g. <code>2026-01-15</code>) — other formats will be rejected.
                <br />
                Category values: <code>{CATEGORY_CODES}</code>
                &nbsp;— any other value is saved as <code>OTHERS</code>.
                <br />
                Type: <code>2</code> = Email &nbsp;|&nbsp; <code>4</code> = WhatsApp &nbsp;|&nbsp; <code>8</code> = Event
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default BulkUploadModal;
